import {EventEmitter, Injectable} from '@angular/core';
import {IncomeSource} from "../../shared/model/income-source";
import {map, tap} from "rxjs/operators";
import {Occupation} from "../../shared/model/occupation";
import {State} from "../../shared/model/state";

import {OnboardingApiControllerService, ProductDetails, ProductView} from "falcon-api";
import {NGXLogger} from "ngx-logger";
import {Country} from "../../shared/model/country";
import {AnnualIncome} from "../../shared/model/annual-income";
import {ValidationDetail} from "../../shared/model/ValidationDetail";
import {BehaviorSubject, Observable, Subject, Subscription, combineLatest} from "rxjs";
import { FormControl, FormControlStatus, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MultipleProductsAccount } from 'src/app/success/success.component';
import { EnrollmentResponse } from 'skyhopper-api';
import { ApplicationLogsRequest, ApplicationProductRequest } from 'leia-api';
import { SecureApplicationReasonEnum } from 'src/app/shared/model/secure-application-reason.enum';
import { ProductType } from '../models/product-type';
import { AccountOptionsValidator } from '../account-selector/account-options.validator';
import { PRODUCT_APY_TEXT_SETTING_KEY } from 'src/app/shared/constants';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { SettingsEnum } from 'src/app/shared/model/settings.enum';

@Injectable({
  providedIn: 'root'
})
export class SignupService {
  static secureReasonsNotAllowMultiproduct = new Map<SecureApplicationReasonEnum, boolean>([
    [SecureApplicationReasonEnum.jointMaintenance, false],
    [SecureApplicationReasonEnum.authenticated, false],
    [SecureApplicationReasonEnum.pwa, false]
  ]);

  public allTermsConditionsAccepted = false;
  public taxPayerCertificationAccepted = false;
  public idleDialogVisible : boolean = false;
  public formElementHasChanged : boolean = false;

  private _forms: FormGroup[] = [];
  private _formStates: Observable<FormControlStatus>[] = [];
  private _formStatesValidSubscription!: Subscription;
  private _allFormSectionsComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  allFormSectionsComplete$: Observable<boolean> = this._allFormSectionsComplete.asObservable();

  private _accountSelection!: FormGroup;
  get accountSelection(): FormGroup {
    return this._accountSelection;
  }

  public allTrackerSectionsComplete: Subject<boolean> = new Subject<boolean>();
  public termsAccepted: Subject<boolean> = new Subject<boolean>();

  private _canAddMultipleAccounts$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  canAddMultipleAccounts$ = this._canAddMultipleAccounts$.asObservable();

  private _canHaveJointAccont$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  canHaveJointAccount$ = this._canHaveJointAccont$.asObservable();

  public reviewSubmissionStatusChange = new EventEmitter<boolean>();
  public validationsChange = new EventEmitter<ValidationDetail>();

  private _multipleProductsAccount : MultipleProductsAccount[] = [];
  get multipleProductsAccount(): MultipleProductsAccount[] {
    return this._multipleProductsAccount;
  }

  constructor(private onboardingApiService: OnboardingApiControllerService,
              private logger: NGXLogger) {
     this._initializeValidFormStateObservable();
  }

  canHaveJointAccount(isAllowed: boolean): void {
    this._canHaveJointAccont$.next(isAllowed);
  }

  mapEnrolledAccounts(enrollmentResponse: EnrollmentResponse, applicationLog: ApplicationLogsRequest, accountConfiguration: ProductView[], productSettings: Map<string, Map<string, unknown>>, linkedAccount: string | undefined): void {
    // Map succesfull accounts
    this._multipleProductsAccount = enrollmentResponse.accounts!
      .filter(({accountNumber}) =>
        UtilitiesService.getLastNCharacters(accountNumber, 4) !== UtilitiesService.getLastNCharacters(linkedAccount, 4)
      ) // Do not include linked accounts
      .map(account => {
        const requestedProduct = applicationLog.products!.find(value => value.applicationProductUID === account.applicationProductUUID)!;
        const { productDescription, productCode, productUID, productType } = requestedProduct;

        return this._buildMultipleProductAccount(
          productDescription,
          productSettings.get(productUID!)!.get(SettingsEnum.ProductStripeColor) as string,
          this._getAccountAPY(productCode!, accountConfiguration),
          productType,
          productSettings.get(productUID!)!.get(PRODUCT_APY_TEXT_SETTING_KEY) as string,
          account.accountNumber,
          true,
        );
      });

    // Map unsuccesfull accounts
    this._multipleProductsAccount.concat(
      applicationLog.products!
        .filter(product => this._getApplicationProductUIDNotEnrolled(enrollmentResponse, applicationLog)!
          .includes(product.applicationProductUID!)
        )
        .map(product => this._buildMultipleProductAccount(product.productDescription)
        )
    );
  }

  private _getAccountAPY(accountCode: string, accountConfiguration: ProductView[]): string | undefined {
    return this._getAccountConfigurationWithAccountCode(accountCode, accountConfiguration)?.apy?.toString();
  }

  private _getAccountConfigurationWithAccountCode(accountCode: string, accountConfiguration: ProductView[]): ProductDetails | undefined{
    let account: ProductDetails | undefined;

    for (let i = 0; i < accountConfiguration.length; i++) {
      account = accountConfiguration[i].products!.find(account => account.code!.toString() === accountCode);
      if (account) { break; }
    }

    return account;
  }

  private _buildMultipleProductAccount(
    productName: string = "",
    productStripeColor: string = "",
    productDescription: string = "",
    productType: string = "",
    accountAPYText: string = "",
    accountNumber: string = "",
    status: boolean = false,
  ): MultipleProductsAccount {
    return {productName, productDescription, productType, accountAPYText, accountNumber, status, productStripeColor};
  }

  private _getApplicationProductUIDNotEnrolled(enrollmentResponse: EnrollmentResponse, applicationLogsRequest: ApplicationLogsRequest): string[] {
    return applicationLogsRequest.products!
      .filter(requestedProduct => !enrollmentResponse.accounts!
        .map(account => account.applicationProductUUID)
        .includes(requestedProduct.applicationProductUID)
      )
      .map(product => product.applicationProductUID!)
  }

  private _initializeValidFormStateObservable() {
    if (this._formStatesValidSubscription) {
      this._formStatesValidSubscription.unsubscribe();
    }

    this._formStatesValidSubscription = combineLatest(this._formStates).pipe(
      map(states => states.every(state => state === 'VALID')),
    ).subscribe(isValid => {
      this._allFormSectionsComplete.next(isValid);
    });
  }

  static createAccountSelectionForm(): FormGroup {
    return new FormGroup({
      accountType: new FormControl("", [Validators.required]),
      account: new FormControl("", [Validators.required]),
      accountCode: new FormControl(""),
      orderDebitCard: new FormControl(false),
      orderDebitCardForPrimary: new FormControl(false),
      orderDebitCardForCoApplicant: new FormControl(false),
      orderChecks: new FormControl(false),
      orderChecksForPrimary: new FormControl(false),
      orderChecksForCoApplicant: new FormControl(false),
      applicationProductUID: new FormControl(""),
      isJoint: new FormControl(false),
    }, AccountOptionsValidator.debitCardApplicantType());
  }

  static createBSAQuestionsForm(validators: Array<ValidatorFn>): FormGroup {
    const formGroup = new FormGroup({
      achTransactions: new FormControl('', [Validators.required]),
      wireDomestic: new FormControl('', [Validators.required]),
      wireInternational: new FormControl('', [Validators.required]),
      cashTransactions: new FormControl('', [Validators.required]),
      useDebitCard: new FormControl(null, [Validators.required]),
      useChecks: new FormControl(null, [Validators.required]),
      orderDebitCard: new FormControl(null),
      orderChecks: new FormControl(null),
      internationalCountry: new FormControl(''),
      countryInput: new FormControl(null),
    }, validators);

    return formGroup;
  }
  
  static getEffectiveDate(): string {
    const date = new Date();
    const dayNameList =['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthName = date.toLocaleString('default', { month: 'long' });
    return `${dayNameList[date.getDay()]}, ${monthName} ${date.getDate()}, ${date.getFullYear()}`;
  }

  addAccountSelectionForm(form: FormGroup): void {
    this._accountSelection = form;
    this.addFormToTrackState(this._accountSelection);
  }

  addFormToTrackState(form: FormGroup): void {
    this._forms.push(form);
    this._formStates.push(form.statusChanges);
    this._initializeValidFormStateObservable();

    this._forms.forEach(form => (<EventEmitter<string>> form.statusChanges).emit(form.status));
  }

  removeFormFromTrackedState(form: FormGroup): void {
    const formIndex = this._forms.indexOf(form);

    if (formIndex === -1) { return; }

    this._formStates.splice(formIndex, 1);
    this._forms.splice(formIndex, 1);
    this._initializeValidFormStateObservable();
    this._forms.forEach(form => (<EventEmitter<string>> form.statusChanges).emit(form.status));
  }

  clearFormStateObservables(): void {
    this._forms = [];
    this._formStates = [];
    this._initializeValidFormStateObservable();
  }

  private _incomeSources$: BehaviorSubject<Array<IncomeSource>> = new BehaviorSubject(new Array());
  /**
   * get all income sources
   */
  getAllIncomeSources(): Array<IncomeSource> {
    return this._incomeSources$.value;
  }

  loadAllIncomeSources(): BehaviorSubject<Array<IncomeSource>> {
    this.onboardingApiService.getAllIncomeSources1().pipe(
      map((items) => items
          .map(item => (new IncomeSource(item.id as number, item.name as string))),
        tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
      )
    ).subscribe((mappedItems) => {
      this._incomeSources$.next(mappedItems);
    });

    return this._incomeSources$;
  }

  private _occupations$: BehaviorSubject<Array<Occupation>> = new BehaviorSubject(new Array());
  /**
   * get all Occupations
   */
  getAllOccupations(): Array<Occupation> {
    return this._occupations$.value;
  }

  loadAllOccupations(): BehaviorSubject<Array<Occupation>> {
    this.onboardingApiService.getAllOccupations().pipe(
      map((items) => items
          .map(item => (new Occupation(item.id as number, item.name as string))),
        tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
      )
    ).subscribe((mappedItems) => {
      this._occupations$.next(mappedItems);
    });

    return this._occupations$;
  }

  private _countries$: BehaviorSubject<Array<Country>> = new BehaviorSubject(new Array());
  /***
   * get all of the countries from Falcon
   */
  getCountries(): Array<Country> {
    return this._countries$.value;
  }

  loadCountries(): BehaviorSubject<Array<Country>> {
    this.onboardingApiService.getAllCountries().pipe(
      map((items) => items
          .map(item => (new Country(item.countryCode!, item.countryName!, item.isofacSanctioned!, item.international!, item.abbreviation1!))),
        tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
      )
    ).subscribe((mappedItems) => {
      this._countries$.next(mappedItems);
    });

    return this._countries$;
  }

  private _annualIncomes$: BehaviorSubject<Array<AnnualIncome>> = new BehaviorSubject(new Array());
  /***
   * get all of the income ranges, sorted by ID (which is smallest to largest)
   */
  getAnnualIncomes(): Array<AnnualIncome> {
    return this._annualIncomes$.value;
  }

  loadAnnualIncomes(): BehaviorSubject<Array<AnnualIncome>> {
    this.onboardingApiService.getAllIncomes().pipe(
      map((items) => items
          .map(item => (new AnnualIncome(item.id, item.minValue, item.maxValue, item.displayText))),
        tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
      )
    ).subscribe((mappedItems) => {
      this._annualIncomes$.next(mappedItems.sort((n1, n2) => {
        if (n1.annualIncomeId > n2.annualIncomeId) return 1;
        if (n1.annualIncomeId < n2.annualIncomeId) return -1;
        return 0;
      }));
    });

    return this._annualIncomes$;
  }

  private _states$: BehaviorSubject<Array<State>> = new BehaviorSubject(new Array());
  /**
   * get all States
   */
  getAllStates(): Array<State> {
    return this._states$.value;
  }

  loadAllStates(): BehaviorSubject<Array<State>> {
    this.onboardingApiService.getAllStates().pipe(
      map((items) => items
          .map(item => (new State(item.stateProvinceId as number, item.countryCode!, item.stateName as string, item.stateAbbreviation as string))),
        tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
      )
    ).subscribe(states => this._states$.next(states));

    return this._states$;
  }

  setIfMultiProductAllowed(isAllowed: boolean): void {
    this._canAddMultipleAccounts$.next(isAllowed);
  }
  
  isMultiProductAllowed(): boolean {
    return this._canAddMultipleAccounts$.getValue();
  }

  static isMultiProductIsAllowed(productUUID: string, configuration: ProductView[], secureApplicationReason: SecureApplicationReasonEnum | undefined): boolean {
    let selectedProduct: ProductDetails | undefined;

    configuration.forEach(type => {
      if (!selectedProduct) {
        selectedProduct = type.products?.find(product => product.productUID === productUUID);
      }
    });

    if (!SignupService.doesSecureApplicationReasonAllowMultiproduct(secureApplicationReason)) {
      return false;
    }

    return !!selectedProduct?.allowMultiProductApplications;
  }

  static doesSecureApplicationReasonAllowMultiproduct(secureApplicationReason: SecureApplicationReasonEnum | undefined): boolean {
    if (!secureApplicationReason) { return true; }

    if (Array.from(SignupService.secureReasonsNotAllowMultiproduct.keys()).includes(secureApplicationReason)) {
      return false;
    }

    return true;
  }

  static updateBSAProperties(
    products: ApplicationProductRequest[],
    bsaValue: BSAValue,
    isInAuthenticatedFlow: boolean,
    isInJointMaintenanceFlow: boolean
  ): void {
    const {
      achTransactions, cashTransactions, countryList, orderChecks, orderDebitCard,
      useChecks, useDebitCard, wireDomestic, wireInternational
    } = bsaValue;
    products.filter(({productType}) => productType === ProductType.Checking).forEach(product => {
      product.achTransactions = achTransactions;
      product.cashTransactions = cashTransactions;
      product.usingChecks = useChecks;
      product.usingDebitCard = useDebitCard;
      product.wireTransferDomestic = wireDomestic;
      product.wireTransferInternational = wireInternational;
      product.wireTransferInternationalCountries = countryList;

      if (isInAuthenticatedFlow || isInJointMaintenanceFlow) {
        product.orderChecks = orderChecks;
        product.orderDebitCard = orderDebitCard;
        product.orderChecksApplicantType = orderChecks ? 1 : 0;
        product.orderDebitApplicantType = orderDebitCard ? 1 : 0;
      }
    });

  }
}

export interface BSAValue {
  achTransactions: boolean,
  cashTransactions: boolean,
  countryList: string,
  orderChecks: boolean,
  orderDebitCard: boolean,
  useDebitCard: boolean,
  useChecks: boolean,
  wireDomestic: boolean,
  wireInternational: boolean,
}
